// Per-service FAQ entries used on /leistungen/:slug pages.
// Each entry: 5–7 informal-Du questions matching how people search on Google
// and ask AI assistants. Answers 2–4 sentences with concrete numbers.

export type ServiceFAQ = { q: string; a: string };

export const SERVICE_FAQS: Record<string, ServiceFAQ[]> = {
  // ─────────────── Einkommensversicherung ───────────────
  "berufsunfaehigkeitsversicherung": [
    {
      q: "Was ist eine Berufsunfähigkeitsversicherung und wozu brauche ich sie?",
      a: "Die Berufsunfähigkeitsversicherung zahlt Dir eine vereinbarte monatliche Rente, wenn Du Deinen Beruf zu mindestens 50 % nicht mehr ausüben kannst. Jeder vierte Berufstätige in Deutschland wird im Laufe seines Lebens berufsunfähig. Ohne Berufsunfähigkeitsversicherung bleibt im Ernstfall nur die gesetzliche Erwerbsminderungsrente von im Schnitt unter 1.000 € im Monat.",
    },
    {
      q: "Ab wann lohnt sich eine Berufsunfähigkeitsversicherung?",
      a: "So früh wie möglich – idealerweise als Schüler, Azubi oder Student. Je jünger und gesünder Du bist, desto niedriger der Beitrag und desto weniger Ausschlüsse oder Risikozuschläge. Wer früh abschließt, sichert sich oft deutlich günstigere Konditionen.",
    },
    {
      q: "Kann ich als Schüler:in oder Azubi schon eine Berufsunfähigkeitsversicherung abschließen?",
      a: "Ja – eine Berufsunfähigkeitsversicherung ist bereits in der Ausbildungsphase abschließbar. Das ist gerade für Risikoberufe ein Muss, weil der Gesundheitszustand meist noch besser ist und die Beiträge dadurch deutlich günstiger ausfallen. Je früher Du einsteigst, desto besser lassen sich auch spätere Einkommenssteigerungen über Nachversicherungsgarantien absichern.",
    },
    {
      q: "Gibt es Berufsunfähigkeitsversicherung auch für Kinder?",
      a: "Ja – einige Versicherer bieten sogenannte Kinder-Berufsunfähigkeitsversicherung an. Sie schützt Dein Kind schon vor dem Berufsstart gegen die finanziellen Folgen einer Berufsunfähigkeit und sichert die spätere Anwartschaft auf eine Erwachsenen-Berufsunfähigkeitsversicherung. Dabei werden kinder-typische Krankheiten und Verletzungen berücksichtigt, ohne dass die spätere Berufstätigkeit bereits feststehen muss.",
    },
    {
      q: "Wie hoch sollte meine Berufsunfähigkeitsrente sein?",
      a: "Faustregel: 70–80 % Deines aktuellen Nettoeinkommens. Das deckt laufende Kosten, Altersvorsorge und Inflation ab. Die meisten Versicherer akzeptieren bis zu 60 % des Bruttoeinkommens als versicherbare Rente.",
    },
    {
      q: "Was kostet eine gute Berufsunfähigkeitsversicherung?",
      a: "Der Beitrag hängt stark vom Beruf, Eintrittsalter, Gesundheitszustand und der gewünschten Rente ab. Ein junger Akademiker zahlt für 2.000 € Berufsunfähigkeitsrente oft zwischen 50 und 90 € monatlich, ein erfahrener Handwerker mit gleicher Rente kann 150–250 € zahlen. Je früher Du abschließt, desto günstiger wird der Beitrag.",
    },
    {
      q: "Worauf muss ich bei einem Berufsunfähigkeitstarif unbedingt achten?",
      a: "Wichtig sind: Verzicht auf abstrakte Verweisung, Prognosezeitraum von 6 Monaten, freie Arztwahl, Nachversicherungsgarantie ohne Gesundheitsprüfung und eine Leistung bis zum Ende des Erwerbslebens. Diese Klauseln entscheiden im Leistungsfall über fünf- bis sechsstellige Summen.",
    },
    {
      q: "Was ist der Unterschied zwischen Berufsunfähigkeitsversicherung und Erwerbsunfähigkeitsversicherung?",
      a: "Die Berufsunfähigkeitsversicherung zahlt, wenn Du Deinen zuletzt ausgeübten Beruf nicht mehr ausüben kannst. Die Erwerbsunfähigkeitsversicherung zahlt erst, wenn Du gar keine Tätigkeit auf dem allgemeinen Arbeitsmarkt mehr ausüben kannst – die Hürde ist deutlich höher. Die Berufsunfähigkeitsversicherung ist daher in fast allen Fällen die bessere Wahl.",
    },
    {
      q: "Werden Vorerkrankungen ein Problem für die Berufsunfähigkeitsversicherung?",
      a: "Nicht unbedingt – aber sie müssen korrekt angegeben werden, sonst riskierst Du den Versicherungsschutz. Wir stellen vor jedem Antrag eine anonyme Risikovoranfrage bei mehreren Versicherern, um Ausschlüsse oder Risikozuschläge zu vermeiden.",
    },
  ],

  "grundfaehigkeitenversicherung": [
    {
      q: "Was ist eine Grundfähigkeitenversicherung?",
      a: "Sie zahlt eine vereinbarte monatliche Rente, wenn Du eine oder mehrere definierte Grundfähigkeiten wie Sehen, Hören, Sprechen, Gehen oder den Gebrauch der Hände dauerhaft verlierst – unabhängig von Deinem Beruf. Die Leistungsdauer beträgt in der Regel mindestens 12 Monate Verlust der Fähigkeit.",
    },
    {
      q: "Was ist der Unterschied zur Berufsunfähigkeitsversicherung?",
      a: "Die Berufsunfähigkeitsversicherung sichert den Verlust Deiner Berufstätigkeit ab, die Grundfähigkeitenversicherung den Verlust körperlicher oder geistiger Fähigkeiten. Die Grundfähigkeiten­versicherung ist deutlich günstiger, leistet aber nur bei klar definierten Verlusten.",
    },
    {
      q: "Für wen lohnt sich eine Grundfähigkeitenversicherung?",
      a: "Vor allem für Handwerker, Pflegekräfte und körperlich Tätige, die in der Berufsunfähigkeitsversicherung teuer oder schwer versicherbar sind. Auch bei Vorerkrankungen, die den Abschluss einer Berufsunfähigkeitsversicherung erschweren, ist sie oft die einzige Alternative.",
    },
    {
      q: "Lohnt sich eine Grundfähigkeitenversicherung statt einer Berufsunfähigkeitsversicherung?",
      a: "Wenn Du eine Berufsunfähigkeitsversicherung zu fairen Konditionen bekommst, ist diese fast immer die bessere Wahl – sie deckt mehr Risiken ab. Die Grundfähigkeitenversicherung ist Plan B, wenn Berufsunfähigkeitsversicherung zu teuer oder nicht möglich ist.",
    },
    {
      q: "Welche Fähigkeiten sind versichert?",
      a: "Typisch sind Sehen, Hören, Sprechen, Hände gebrauchen, Gehen, Treppensteigen, Stehen, Autofahren sowie die geistige Leistungsfähigkeit. Die genauen Definitionen unterscheiden sich je nach Anbieter stark – ein guter Tarif sollte mindestens 15 Grundfähigkeiten klar definieren.",
    },
    {
      q: "Wie hoch sollte die Rente sein?",
      a: "Ähnlich wie bei der Berufsunfähigkeitsversicherung: 70–80 % des Nettoeinkommens. Bei sehr körperlichen Berufen oft auch als reine Existenzsicherung, z. B. 1.500 € monatlich.",
    },
  ],

  "risikolebensversicherung": [
    {
      q: "Was ist eine Risikolebensversicherung?",
      a: "Sie zahlt im Todesfall die vereinbarte Versicherungssumme an Deine Hinterbliebenen – steuerfrei und schnell. Im Gegensatz zur Kapitallebensversicherung gibt es keine Ansparkomponente und keine Auszahlung am Ende der Laufzeit.",
    },
    {
      q: "Wie hoch sollte meine Versicherungssumme sein?",
      a: "Faustregel: 3–5 Brutto-Jahresgehälter plus offene Restschulden (z. B. Hypothek). Für eine Familie mit Kindern oft 200.000 € bis 500.000 €. Bei Immobilienfinanzierungen mindestens in Höhe der Restschuld.",
    },
    {
      q: "Was kostet eine Risikolebensversicherung?",
      a: "Sehr wenig: Ein 30-jähriger Nichtraucher zahlt für 250.000 € Versicherungssumme über 30 Jahre oft nur 8–15 € monatlich. Raucher zahlen etwa das Doppelte.",
    },
    {
      q: "Lohnt sich eine verbundene Police für Paare?",
      a: "Für verheiratete Paare oft ja – ein gemeinsamer Vertrag ist günstiger. Für unverheiratete Paare ist eine Über-Kreuz-Police meist besser, da der erbschaftsteuerliche Freibetrag nur 20.000 € beträgt und sonst Steuern auf die Auszahlung anfallen.",
    },
    {
      q: "Wann sollte ich eine Risikoleben abschließen?",
      a: "Spätestens beim ersten Kind, bei Heirat oder bei einer Immobilienfinanzierung. Je früher und gesünder Du abschließt, desto günstiger der Beitrag – der Tarif gilt dann meist 20–30 Jahre unverändert.",
    },
    {
      q: "Ist die Auszahlung steuerfrei?",
      a: "Für Ehepartner und Kinder ja, dank hoher Freibeträge (500.000 € bzw. 400.000 €). Für Unverheiratete liegt der Freibetrag nur bei 20.000 € – darüber wird Erbschaftsteuer fällig. Lösung: Über-Kreuz-Versicherung.",
    },
  ],

  // ─────────────── Sachversicherungen ───────────────
  "privathaftpflicht": [
    {
      q: "Brauche ich wirklich eine Privathaftpflicht?",
      a: "Ja – sie ist die wichtigste Versicherung überhaupt. Wer einen Personenschaden verursacht, haftet mit dem gesamten Vermögen, oft lebenslang. Bereits ein schwerer Personenschaden kann mehrere Millionen Euro kosten.",
    },
    {
      q: "Wie viel kostet eine gute Privathaftpflicht?",
      a: "Gute Tarife gibt es ab 50–80 € im Jahr für Singles, Familien zahlen etwa 70–120 € jährlich. Im Verhältnis zum Risiko ist das ein Bruchteil dessen, was Du dafür an Schutz bekommst.",
    },
    {
      q: "Wie hoch sollte die Deckungssumme sein?",
      a: "Mindestens 10 Mio. €, besser 50 Mio. € pauschal für Personen-, Sach- und Vermögensschäden. Bei modernen Tarifen ist das ohne Aufpreis Standard.",
    },
    {
      q: "Sind meine Kinder mitversichert?",
      a: "Ja – minderjährige Kinder sind in der Familienpolice immer mitversichert. Volljährige Kinder bleiben mitversichert, solange sie sich in Ausbildung oder Erststudium befinden. Wichtig ist außerdem die Klausel zur Mitversicherung von Schäden deliktunfähiger Kinder unter 7 Jahren.",
    },
    {
      q: "Was ist mit Schäden durch Gefälligkeit?",
      a: "Hilfst Du beim Umzug und beschädigst etwas, bist Du juristisch oft gar nicht haftbar – moralisch aber schon. Top-Tarife übernehmen sogenannte Gefälligkeitsschäden bis zu 5.000–10.000 €, schwächere Tarife nicht.",
    },
    {
      q: "Ist Schlüsselverlust mitversichert?",
      a: "Nur in Tarifen mit dem Baustein Schlüsselverlust. Wichtig vor allem für Mieter und Berufstätige mit Generalschlüssel – der Austausch einer Schließanlage kann schnell 5.000–20.000 € kosten.",
    },
  ],

  "hausratversicherung": [
    {
      q: "Was deckt eine Hausratversicherung ab?",
      a: "Sie ersetzt Möbel, Elektronik, Kleidung und alle beweglichen Gegenstände bei Feuer, Einbruchdiebstahl, Leitungswasser sowie Sturm und Hagel. Die Erstattung erfolgt zum Neuwert – Du bekommst also das Geld für eine vergleichbare Neuanschaffung.",
    },
    {
      q: "Wie hoch sollte die Versicherungssumme sein?",
      a: "Faustregel: 650 € pro m² Wohnfläche – dann gilt der Unterversicherungs­verzicht. Bei hochwertiger Einrichtung individuell berechnen, sonst kürzt der Versicherer im Schadenfall anteilig.",
    },
    {
      q: "Ist mein Fahrrad mitversichert?",
      a: "Innerhalb der Wohnung ja, außerhalb nur mit dem Baustein Fahrraddiebstahl. Üblich sind 1–10 % der Versicherungssumme – bei E-Bikes lohnt sich oft eine eigene Fahrradversicherung mit Vollkaskoschutz.",
    },
    {
      q: "Was ist mit Elementarschäden wie Hochwasser?",
      a: "Hochwasser, Starkregen, Rückstau, Schneedruck und Erdrutsch sind NICHT im Grundtarif enthalten und müssen extra abgeschlossen werden. Angesichts häufigerer Starkregen-Ereignisse ist Elementarschutz heute fast überall sinnvoll – Mehrkosten meist 30–80 € jährlich.",
    },
    {
      q: "Wann lohnt sich eine Hausratversicherung?",
      a: "Sobald Du eine eigene Wohnung mit nennenswerter Einrichtung hast. Bei Studenten reicht oft die Mitversicherung über die Police der Eltern. Für Erwachsene mit Möbeln, Elektronik und Kleidung im Wert von mehreren tausend Euro ist sie sehr sinnvoll.",
    },
    {
      q: "Bin ich auch auf Reisen geschützt?",
      a: "Ja – über die sogenannte Außenversicherung. Hausrat, der vorübergehend nicht zu Hause ist (z. B. im Hotel oder in der Studentenbude), bleibt meist 3 Monate weltweit mitversichert.",
    },
  ],

  "wohngebaeudeversicherung": [
    {
      q: "Was deckt eine Wohngebäudeversicherung ab?",
      a: "Sie schützt die Bausubstanz des Hauses sowie fest verbaute Bestandteile (Heizung, Sanitär, Einbauschränke) bei Feuer, Leitungswasser und Sturm/Hagel. Elementarschäden wie Hochwasser oder Starkregen sind als Zusatzbaustein wichtig.",
    },
    {
      q: "Ist eine Wohngebäudeversicherung Pflicht?",
      a: "Gesetzlich nein – aber bei einer Immobilienfinanzierung verlangt die Bank fast immer mindestens eine Feuerversicherung. Ohne Schutz riskierst Du bei einem Totalschaden den Verlust Deines gesamten Eigenkapitals.",
    },
    {
      q: "Was kostet eine Wohngebäudeversicherung?",
      a: "Je nach Wohnort, Bauart und Gebäudewert zwischen 200 und 800 € jährlich. Standorte mit erhöhtem Hochwasserrisiko (ZÜRS-Zone 3 oder 4) kosten deutlich mehr.",
    },
    {
      q: "Lohnt sich der Baustein Elementarschäden?",
      a: "Ja – fast überall in Deutschland. Starkregen, Rückstau und Überschwemmungen sind heute Top-Schadenursachen und nehmen durch den Klimawandel zu. Mehrkosten meist 50–300 € jährlich, je nach Region.",
    },
    {
      q: "Was ist der gleitende Neuwert?",
      a: "Die Versicherungssumme wird jährlich an die Baupreisentwicklung angepasst. So bleibt das Haus auch nach 20 Jahren vollwertig versichert – ohne dass Du selbst nachrechnen musst.",
    },
    {
      q: "Sind Photovoltaikanlagen mitversichert?",
      a: "Standardmäßig nur in begrenztem Umfang. Für PV-Anlagen empfiehlt sich entweder eine Erweiterung der Wohngebäudepolice oder eine eigene Photovoltaik­versicherung mit Ertragsausfall.",
    },
  ],

  "kfz-versicherung": [
    {
      q: "Was ist der Unterschied zwischen Teilkasko und Vollkasko?",
      a: "Teilkasko deckt Schäden durch Diebstahl, Glasbruch, Wildunfall, Sturm, Hagel und Marderbiss. Vollkasko zusätzlich selbstverschuldete Unfälle, Vandalismus und Schäden durch Fahrerflucht. Vollkasko ist meist für Fahrzeuge bis 5 Jahre oder mit hohem Wert sinnvoll.",
    },
    {
      q: "Wie hoch sollte die Haftpflicht-Deckungssumme sein?",
      a: "Mindestens 100 Mio. € pauschal. Die gesetzliche Mindestdeckung von 7,5 Mio. € reicht bei schweren Personenschäden oft nicht aus – moderne Tarife bieten 100 Mio. € ohne Mehrkosten.",
    },
    {
      q: "Wie kann ich bei der KFZ-Versicherung sparen?",
      a: "Werkstattbindung (bis 20 % Ersparnis), jährliche Zahlweise (bis 5 %), realistische Kilometerangabe, niedriges Eintrittsalter durch Übernahme der SF-Klasse innerhalb der Familie und jährlicher Tarifvergleich zum Stichtag 30.11.",
    },
    {
      q: "Wann sollte ich den KFZ-Versicherer wechseln?",
      a: "Stichtag ist meist der 30.11. – Kündigung muss bis dahin vorliegen. Ein jährlicher Vergleich spart oft 100–500 € pro Jahr, gerade bei langer Vertragslaufzeit oder veränderten Lebensumständen (Umzug, neue Garage, weniger Kilometer).",
    },
    {
      q: "Was ist ein Telematik-Tarif?",
      a: "Ein Tarif, bei dem Dein Fahrverhalten per App gemessen wird (Geschwindigkeit, Bremsen, Kurven). Wer defensiv fährt, spart bis zu 30 % – besonders attraktiv für Fahranfänger mit hoher SF-Klasse.",
    },
    {
      q: "Lohnt sich ein Rabattschutz?",
      a: "Für Vielfahrer und Pendler oft ja. Mit Rabattschutz wirst Du nach einem Schaden pro Jahr nicht zurückgestuft – die Beitragsersparnis nach einem Unfall ist meist deutlich höher als die Mehrkosten von 30–80 € jährlich.",
    },
  ],

  "unfallversicherung": [
    {
      q: "Wann zahlt die private Unfallversicherung?",
      a: "Bei bleibender Beeinträchtigung (Invalidität) nach einem Unfall – innerhalb eines Jahres entstanden und innerhalb von 15 Monaten festgestellt. Die Höhe der Auszahlung richtet sich nach der Gliedertaxe und dem Invaliditätsgrad.",
    },
    {
      q: "Warum brauche ich eine private Unfallversicherung?",
      a: "Die gesetzliche Unfallversicherung greift nur am Arbeitsplatz und auf dem Arbeitsweg. Eine private Unfallversicherung ergänzt diesen Schutz für Freizeit und Alltag, damit Du nach einem Unfall mit bleibender Beeinträchtigung finanziell abgesichert bist.",
    },
    {
      q: "Wie hoch sollte die Invaliditätssumme sein?",
      a: "Mindestens das 5- bis 6-fache des Brutto-Jahreseinkommens, also oft 200.000–500.000 €. Mit Progression (z. B. 500 %) kann die Auszahlung bei schwerer Invalidität entsprechend höher ausfallen.",
    },
    {
      q: "Was ist Progression bei der Unfallversicherung?",
      a: "Bei höheren Invaliditätsgraden vervielfacht sich die Versicherungssumme – z. B. 225, 350 oder 500 %. So erreichst Du auch bei kleinerer Grundsumme eine angemessene Auszahlung bei schwerer Behinderung.",
    },
    {
      q: "Ersetzt die Unfallversicherung eine Berufsunfähigkeitsversicherung?",
      a: "Nein – sie ist kein Ersatz, sondern eine Ergänzung. Eine Berufsunfähigkeitsversicherung leistet bei Berufsunfähigkeit aus jedem Grund (vor allem Krankheit), die Unfallversicherung nur nach Unfällen. 90 % aller Berufsunfähigkeiten haben nichts mit Unfällen zu tun.",
    },
    {
      q: "Lohnt sich Unfallversicherung für Kinder?",
      a: "Ja – Kinder sind über die gesetzliche Unfallversicherung nur in Kita, Schule und Hin- und Rückweg geschützt. Eine private Unfallversicherung schließt die Lücke für die übrige Freizeit und ist mit 60–150 € jährlich erschwinglich.",
    },
  ],

  "tierhalterhaftpflicht": [
    {
      q: "Brauche ich als Hundehalter eine Tierhalterhaftpflicht?",
      a: "In Berlin, Hamburg, Niedersachsen, Sachsen-Anhalt, Schleswig-Holstein und Thüringen ist sie für Hunde Pflicht. In allen anderen Bundesländern dringend zu empfehlen, da Tierhalter verschuldensunabhängig haften (§ 833 BGB).",
    },
    {
      q: "Was kostet eine Hundehalterhaftpflicht?",
      a: "Gute Tarife gibt es ab 50–90 € jährlich für einen Hund. Listenhunde oder bestimmte Rassen kosten oft 100–200 € jährlich – Spezialtarife sind meist günstiger als Standardanbieter.",
    },
    {
      q: "Sind Katzen in der Tierhalterhaftpflicht enthalten?",
      a: "Nein – für Katzen und Kleintiere ist keine separate Tierhalterhaftpflicht nötig, sie sind automatisch über die Privathaftpflicht abgedeckt. Eine eigene Police brauchst Du nur für Hunde, Pferde und exotische Tiere.",
    },
    {
      q: "Wie hoch sollte die Deckungssumme sein?",
      a: "Mindestens 10 Mio. €, besser 20–50 Mio. €. Personenschäden – etwa wenn der Hund einen Radfahrer zu Fall bringt – können schnell sechsstellige Summen erreichen.",
    },
    {
      q: "Bin ich auch beim Hundetraining oder bei Hundesittern versichert?",
      a: "Top-Tarife schließen sogenannte Hüter- und Hundetrainerschäden mit ein. Ohne diese Klausel ist Dein Hund nicht versichert, sobald jemand anderes auf ihn aufpasst – ein wichtiges Detail beim Tarifvergleich.",
    },
    {
      q: "Was ist mit Mietsachschäden in der Ferienwohnung?",
      a: "Kratzer im Parkett der Ferienwohnung oder Schäden in der Hundepension sind in guten Tarifen mitversichert. Achte beim Vergleich auf den Baustein Mietsachschäden mit ausreichender Summe.",
    },
  ],

  "rechtsschutzversicherung": [
    {
      q: "Welche Bereiche deckt eine Rechtsschutzversicherung ab?",
      a: "Typische Bausteine sind Privat-, Berufs-, Verkehrs- und Mietrechtsschutz. Optional Steuer-, Sozial- und Vermieter-Rechtsschutz. Mit Abstand häufigster Anwendungsfall ist das Arbeitsrecht (Kündigung, Abfindung, Zeugnis).",
    },
    {
      q: "Was kostet eine gute Rechtsschutzversicherung?",
      a: "Für Singles meist 200–350 € jährlich, für Familien 250–450 €. Eine niedrige Selbstbeteiligung von 150 € ist ein guter Kompromiss zwischen Beitrag und Eigenanteil im Schadenfall.",
    },
    {
      q: "Gibt es eine Wartezeit?",
      a: "Ja – im Arbeits-, Miet-, Sozial- und Steuerrecht meist 3 Monate. Im Verkehrsrecht und Strafrecht greift der Schutz oft sofort. Wichtig: Streitigkeiten, die schon vor Vertragsabschluss bekannt waren, sind nie versichert.",
    },
    {
      q: "Wann lohnt sich eine Rechtsschutzversicherung?",
      a: "Vor allem für Berufstätige, Mieter und Autofahrer. Ein einziger Prozess im Arbeitsrecht über zwei Instanzen kann 5.000–15.000 € kosten – die Versicherung amortisiert sich also bereits beim ersten Fall.",
    },
    {
      q: "Was ist Mediation und wozu brauche ich sie?",
      a: "Mediation ist eine außergerichtliche Konfliktlösung mit neutralem Vermittler – meist schneller, günstiger und beziehungsschonender als ein Gerichtsverfahren. Top-Tarife übernehmen die Kosten bis 1.500–3.000 € pro Fall.",
    },
    {
      q: "Bin ich auch im Ausland geschützt?",
      a: "Ja – meist innerhalb der EU unbegrenzt, weltweit oft bis zu 3–12 Monate pro Reise. Wichtig vor allem für Vielreisende und Personen mit Immobilieneigentum im Ausland.",
    },
  ],

  // ─────────────── Gesundheitsvorsorge ───────────────
  "private-krankenvollversicherung": [
    {
      q: "Was ist der Unterschied zwischen PKV und GKV?",
      a: "In der GKV zahlst Du einen einkommensabhängigen Beitrag und erhältst Standardleistungen. In der PKV zahlst Du einen risikoabhängigen Beitrag (Alter, Gesundheit) und kannst Leistungen individuell wählen. Die PKV bietet meist bessere Leistungen, kann aber im Alter teurer werden.",
    },
    {
      q: "Wann kann ich in die PKV wechseln?",
      a: "Als Angestellter, wenn Du über der Versicherungspflichtgrenze (2026: 77.400 € brutto/Jahr, 6.450 €/Monat) verdienst. Als Selbstständiger jederzeit. Beamte können unabhängig vom Einkommen wechseln und nutzen die Beihilfe.",
    },
    {
      q: "Kann ich von der PKV zurück in die GKV wechseln?",
      a: "Sehr schwer und nur unter engen Voraussetzungen: meist nur durch Wechsel in eine sozialversicherungspflichtige Anstellung unter der Versicherungspflichtgrenze und vor dem 55. Lebensjahr. Nach 55 ist die Rückkehr in die GKV faktisch ausgeschlossen.",
    },
    {
      q: "Was passiert mit meiner PKV im Rentenalter?",
      a: "Der Beitrag bleibt bestehen, sinkt aber meist um den Arbeitnehmer-Anteil. Wichtig sind Altersrückstellungen und der gesetzliche Zuschlag von 10 %, der ab 60 entfällt. Mit einer Beitragsentlastungs­komponente lässt sich der Rentenbeitrag um mehrere hundert Euro pro Monat reduzieren.",
    },
    {
      q: "Lohnt sich PKV für Familien?",
      a: "Meist nicht – jedes Familienmitglied braucht einen eigenen Beitrag. In der GKV sind Ehepartner ohne Einkommen und Kinder beitragsfrei mitversichert. Für Familien mit nicht erwerbstätigem Partner und mehreren Kindern ist die GKV fast immer günstiger.",
    },
    {
      q: "Wie finde ich den richtigen PKV-Tarif?",
      a: "Achte auf vertraglich garantierte Leistungen (nicht nur Kulanz), historische Beitragsstabilität, ausreichende Altersrückstellungen und – als Beamter – auf beihilfekonforme Bausteine. Vor dem Antrag führen wir eine anonyme Risikovoranfrage durch, um Ausschlüsse zu vermeiden.",
    },
    {
      q: "Wie viel kann ich mit der PKV sparen?",
      a: "Junge gutverdienende Singles zahlen oft 300–500 € monatlich statt 700–900 € in der GKV – bei deutlich besseren Leistungen. Das eingesparte Geld in einer Beitragsentlastung anzulegen sichert bezahlbare Beiträge im Alter.",
    },
  ],

  "zahnzusatzversicherung": [
    {
      q: "Lohnt sich eine Zahnzusatzversicherung?",
      a: "Für gesetzlich Versicherte fast immer ja: Die GKV zahlt nur einen Festzuschuss für Zahnersatz. Ein Implantat kostet 2.000–4.000 € – davon übernimmt die GKV oft nur 300–500 €. Die Zahnzusatzversicherung kann den Rest bis zu 100 % abdecken.",
    },
    {
      q: "Wann sollte ich eine Zahnzusatzversicherung abschließen?",
      a: "So früh wie möglich, am besten bevor Probleme bekannt sind. Angeratene oder begonnene Behandlungen sind nie versichert. Bei Kindern lohnt sich ein früher Abschluss wegen möglicher Kieferorthopädie.",
    },
    {
      q: "Was ist eine Zahnstaffel?",
      a: "Eine Begrenzung der Leistungen in den ersten Versicherungsjahren – z. B. 1.000 € im 1. Jahr, 2.000 € im 2. Jahr usw. Top-Tarife verzichten auf diese Staffel oder bieten höhere Limits.",
    },
    {
      q: "Was kostet eine gute Zahnzusatzversicherung?",
      a: "Je nach Alter und Leistungsumfang 15–40 € monatlich. Für Kinder oft schon ab 8–15 € monatlich – inklusive Kieferorthopädie und professioneller Zahnreinigung.",
    },
    {
      q: "Ist professionelle Zahnreinigung mitversichert?",
      a: "In guten Tarifen ja – 1 bis 2 Mal pro Jahr ohne Eigenanteil. Eine professionelle Zahnreinigung kostet sonst 80–150 € und ist die wichtigste Vorsorge gegen Karies und Parodontose.",
    },
    {
      q: "Übernimmt die Zahnzusatz auch Kieferorthopädie?",
      a: "Bei Kindern ja, sofern frühzeitig abgeschlossen. Die GKV zahlt nur ab Kieferorthopädischer Indikationsgruppe (KIG) 3 – die Zusatz übernimmt auch bei KIG 1 und 2 die Kosten. Bei Erwachsenen ist KFO meist nur sehr begrenzt versicherbar.",
    },
  ],

  "krankentagegeld": [
    {
      q: "Was ist eine Krankentagegeldversicherung?",
      a: "Sie zahlt Dir ein vereinbartes Tagegeld bei längerer Krankheit – als Einkommensersatz, wenn Lohnfortzahlung oder Krankengeld nicht mehr ausreichen. Wichtig vor allem für Selbstständige und Gutverdiener in der GKV.",
    },
    {
      q: "Wann lohnt sich Krankentagegeld?",
      a: "Selbstständige brauchen es ab Tag 1 oder 15, da es keine Lohnfortzahlung gibt. Angestellte mit Brutto über der Beitragsbemessungs­grenze haben ab Tag 43 eine Lücke zwischen Nettoeinkommen und gesetzlichem Krankengeld – diese Lücke kann mehrere hundert Euro monatlich betragen.",
    },
    {
      q: "Wie hoch sollte das Tagegeld sein?",
      a: "So hoch wie Dein Nettoeinkommen, jedoch nicht höher – sonst kürzt der Versicherer im Schadenfall. Selbstständige rechnen ihr Nettoeinkommen aus dem letzten Steuerbescheid hoch, Angestellte ergänzen das gesetzliche Krankengeld bis zum Netto.",
    },
    {
      q: "Was ist der richtige Karenztag?",
      a: "Angestellte wählen meist ab Tag 43 (nach Lohnfortzahlung). Selbstständige je nach Rücklagen ab Tag 15, 22 oder 43 – je länger die Karenz, desto günstiger der Beitrag.",
    },
    {
      q: "Was kostet Krankentagegeld?",
      a: "Stark abhängig von Alter, Beruf und Karenzzeit. Ein 35-jähriger Selbstständiger zahlt für 150 € Tagegeld ab Tag 43 oft 30–60 € monatlich – ab Tag 15 etwa das Doppelte.",
    },
    {
      q: "Was passiert, wenn aus der Krankheit eine Berufsunfähigkeit wird?",
      a: "Sobald Berufsunfähigkeit festgestellt wird, endet die Krankentagegeld­zahlung. Wichtig ist daher die Kopplung mit einer Berufsunfähigkeitsversicherung – im Idealfall ohne Lücke. Ein guter Tarif zahlt auch ohne erneute Gesundheitsprüfung weiter, bis die Berufsunfähigkeitsversicherung greift.",
    },
  ],

  // ─────────────── Altersvorsorge & Vermögensaufbau ───────────────
  "private-rentenversicherung": [
    {
      q: "Was ist die Rentenlücke und wie berechne ich sie?",
      a: "Die Rentenlücke ist die Differenz zwischen Deinem aktuellen Nettoeinkommen und der voraussichtlichen gesetzlichen Rente. Im Schnitt deckt die gesetzliche Rente nur 48 % des letzten Nettoeinkommens – die Lücke liegt für die meisten Arbeitnehmer bei 700–1.200 € monatlich.",
    },
    {
      q: "Rürup oder Riester – was passt zu mir?",
      a: "Riester eignet sich vor allem für Familien mit Kindern (300 € Zulage pro Kind) und Geringverdiener. Rürup ist ideal für Selbstständige und Gutverdiener mit hohem Steuersatz, da Beiträge 2026 bis 30.826 € jährlich (Ledige) voll absetzbar sind. Wir prüfen kostenlos welche Variante für Dich am meisten bringt.",
    },
    {
      q: "Ist eine ETF-basierte Altersvorsorge sinnvoll?",
      a: "Ja – langfristig bietet eine ETF-Fondspolice deutlich höhere Renditechancen als klassische Rentenversicherungen. Über 30 Jahre können bei 7 % statt 2 % Rendite aus 200 € monatlich 240.000 € statt 100.000 € werden. Wichtig sind niedrige Kosten und breite ETF-Auswahl.",
    },
    {
      q: "Wie sicher ist die gesetzliche Rente?",
      a: "Die gesetzliche Rente ist sicher in dem Sinne, dass sie weiter ausgezahlt wird – aber das Rentenniveau sinkt seit Jahren. Bis 2030 wird es auf 43 % des letzten Nettoeinkommens sinken. Wer den Lebensstandard halten will, muss die Differenz aus privater und betrieblicher Vorsorge schließen.",
    },
    {
      q: "Was ist der Unterschied zwischen bAV und privater Altersvorsorge?",
      a: "Die bAV läuft über den Arbeitgeber – mit Steuer- und Sozialabgabenersparnis und mindestens 15 % Arbeitgeberzuschuss. Private Rentenversicherungen schließt Du eigenständig ab und nimmst sie bei Jobwechsel mit. In der Praxis sind beide oft sinnvoll und ergänzen sich.",
    },
    {
      q: "Ab wann sollte ich mit der Altersvorsorge beginnen?",
      a: "So früh wie möglich. Wer mit 25 anfängt 200 € monatlich zu investieren, hat mit 65 bei 7 % Rendite über 520.000 €. Wer erst mit 35 anfängt, kommt auf weniger als die Hälfte – obwohl der Unterschied nur 10 Jahre ist.",
    },
    {
      q: "Wie hoch ist die Steuerersparnis bei der privaten Rentenversicherung?",
      a: "In der Ansparphase fallen keine Abgeltungsteuer und kein Sparer-Pauschbetrag-Verbrauch an – das beschleunigt den Zinseszinseffekt. Bei Kapitalauszahlung nach 12 Jahren Laufzeit und ab Alter 62 wird nur die Hälfte der Erträge mit dem persönlichen Steuersatz besteuert (Halbeinkünfteverfahren).",
    },
  ],

  "ruerup-rente": [
    {
      q: "Was ist die Rürup-Rente und wer sollte sie abschließen?",
      a: "Die Rürup-Rente (Basisrente) ist eine staatlich geförderte Altersvorsorge bei der Beiträge 2026 bis zu 30.826 € jährlich (Ledige) bzw. 61.652 € (Zusammenveranlagte) vollständig von der Steuer absetzbar sind. Besonders attraktiv für Selbstständige ohne gesetzliche Rentenversicherung und für Angestellte mit hohem Steuersatz ab ca. 40.000 € Jahreseinkommen.",
    },
    {
      q: "Wie funktioniert die Rürup-Rente als Steuersparmodell?",
      a: "Du zahlst Beiträge ein, die als Sonderausgaben in der Steuererklärung geltend gemacht werden. Bei einem Spitzensteuersatz von 42 % bekommst Du also fast die Hälfte Deiner Beiträge über die Steuererstattung zurück. Aus 10.000 € Einzahlung werden netto oft nur 5.800 €.",
    },
    {
      q: "Welche Ausgaben kann ich als Arbeitnehmer von der Steuer absetzen?",
      a: "Werbungskosten (Pendlerpauschale, Homeoffice, Arbeitsmittel, Fortbildung), Vorsorgeaufwendungen (Rürup, GKV/PKV), außergewöhnliche Belastungen, Handwerkerleistungen und haushaltsnahe Dienstleistungen. Mit Rürup lassen sich die Vorsorgeaufwendungen am stärksten optimieren.",
    },
    {
      q: "Was ist der Unterschied zwischen Rürup und privater Rentenversicherung?",
      a: "Rürup-Beiträge sind in der Ansparphase steuerlich absetzbar, dafür ist die Auszahlung in der Rente voll steuerpflichtig (nachgelagerte Besteuerung). Die private Rentenversicherung bietet das Halbeinkünfteverfahren bei Kapitalauszahlung und ein Kapitalwahlrecht. Rürup zahlt nur lebenslange Rente.",
    },
    {
      q: "Kann ich mein Rürup-Kapital vorzeitig auszahlen lassen?",
      a: "Nein – Rürup ist eine reine Rentenversicherung ohne Kapitalwahlrecht. Auszahlung beginnt frühestens mit Alter 62 als lebenslange Rente. Das Kapital ist dafür pfändungs- und Hartz-IV-sicher in der Ansparphase.",
    },
    {
      q: "Was passiert mit der Rürup-Rente im Todesfall?",
      a: "Standardmäßig verfällt das Kapital. Mit einer Hinterbliebenenrente kann Dein Ehepartner oder kindergeldberechtigte Kinder eine Rente erhalten. Andere Erben sind nicht möglich – das ist ein wichtiger Nachteil gegenüber privaten Rentenversicherungen.",
    },
    {
      q: "Welche Rendite kann ich von einer Rürup-Rente erwarten?",
      a: "Bei ETF-basierten Rürup-Tarifen langfristig 5–8 % p. a. plus den Steuervorteil als zusätzlicher Hebel. Wichtig sind niedrige Effektivkosten (unter 1 % p. a.) und eine breite ETF-Auswahl – sonst frisst die Kostenquote den Steuervorteil auf.",
    },
  ],

  "betriebliche-altersvorsorge": [
    {
      q: "Was ist die betriebliche Altersvorsorge (bAV) und lohnt sie sich?",
      a: "Bei der bAV wandelst Du einen Teil Deines Bruttogehalts in Rentenansprüche um – vor Steuern und Sozialabgaben. 2026 sind bis 676 €/Monat (8 % der BBG, § 3 Nr. 63 EStG) lohnsteuerfrei; davon – nicht zusätzlich dazu – sind die ersten 338 €/Monat (4 %, § 1 SvEV) auch sozialabgabenfrei. Mit dem gesetzlichen Arbeitgeberzuschuss von mind. 15 % zahlst Du netto oft nur 50–55 % des Bruttobeitrags.",
    },
    {
      q: "Wie hoch ist der Arbeitgeberzuschuss zur bAV?",
      a: "Seit 2019 mindestens 15 % auf den umgewandelten Beitrag, sofern der Arbeitgeber Sozialversicherung spart. Viele Arbeitgeber zahlen aber freiwillig 20–50 % oder mehr – das ist der wichtigste Hebel für die Höhe Deiner Betriebsrente.",
    },
    {
      q: "Was sind die Nachteile der bAV?",
      a: "In der Rentenphase fallen volle Krankenversicherungsbeiträge an (ca. 18 %) – das verringert die Netto-Rente deutlich. Außerdem sind die Auszahlungen voll einkommensteuerpflichtig. Sinnvoll ist die bAV dennoch fast immer, wenn der Arbeitgeberzuschuss hoch ist.",
    },
    {
      q: "Kann ich die bAV beim Jobwechsel mitnehmen?",
      a: "Ja – seit 2005 ist die bAV portabel. Idealerweise wird der Vertrag beim alten Arbeitgeber beitragsfrei gestellt oder zum neuen Arbeitgeber übertragen. Bei großen Differenzen lohnt sich oft ein neuer Vertrag plus Weiterführung des alten.",
    },
    {
      q: "Welche Durchführungswege der bAV gibt es?",
      a: "Direktversicherung (häufigster Weg), Pensionskasse, Pensionsfonds, Unterstützungskasse und Direktzusage. Für die meisten Arbeitnehmer ist die Direktversicherung als ETF-basierte Fondspolice die beste Wahl.",
    },
    {
      q: "Sollte ich die Standard-bAV meines Arbeitgebers nehmen?",
      a: "Nicht ungeprüft. Viele Rahmenverträge sind Altprodukte mit hohen Kosten von 2–3 % p. a. Wir prüfen den Bestandsvertrag und vergleichen mit modernen Fondspolicen – ein Wechsel innerhalb desselben Durchführungswegs ist meist möglich und spart oft Zehntausende über die Laufzeit.",
    },
  ],

  "etf-sparplan": [
    {
      q: "Was sind ETFs und wie funktionieren sie?",
      a: "ETFs (Exchange Traded Funds) sind börsengehandelte Indexfonds, die einen Markt 1:1 abbilden – z. B. den MSCI World mit 1.500 Unternehmen weltweit. Sie sind kostengünstig (ab 0,07 % p. a.), breit gestreut und transparent.",
    },
    {
      q: "Wie viel Geld brauche ich für einen ETF-Sparplan?",
      a: "Bei den meisten Brokern bereits ab 25 € monatlich. Entscheidend ist nicht die Höhe des Betrags, sondern die Regelmäßigkeit und der lange Anlagehorizont. Über 30 Jahre können aus 100 € monatlich bei 7 % Rendite über 120.000 € werden.",
    },
    {
      q: "Welche ETFs sind für Anfänger geeignet?",
      a: "Klassiker sind der MSCI World (1.500 Unternehmen aus Industrieländern) oder der FTSE All-World (4.000 Unternehmen inkl. Schwellenländer). Beide bieten breite Streuung und solide Renditen von historisch 7–9 % p. a. über lange Zeiträume.",
    },
    {
      q: "Was kostet ein ETF-Sparplan?",
      a: "Bei vielen Brokern fallen pro Sparplan-Ausführung 0–1,50 € an, plus die laufenden ETF-Kosten von 0,07–0,30 % p. a. Im Vergleich zu aktiv verwalteten Fonds mit 1,5–2,5 % p. a. ist das ein massiver Renditevorteil über lange Zeiträume.",
    },
    {
      q: "Wie hoch ist die Steuer auf ETF-Erträge?",
      a: "In Deutschland gilt die Abgeltungsteuer von 25 % plus Soli und ggf. Kirchensteuer. Der Sparer-Pauschbetrag von 1.000 € pro Person ist steuerfrei. Eine Vorabpauschale wird seit 2018 jährlich erhoben – bei ETFs in der Rentenversicherung entfällt diese komplett.",
    },
    {
      q: "Sollte ich besser einmalig anlegen oder einen Sparplan einrichten?",
      a: "Statistisch gesehen ist Einmalanlage über lange Zeiträume etwas profitabler (ca. 60 % der Fälle), psychologisch ist der Sparplan oft besser – Du investierst auch bei Kursrückgängen und mittelst den Kaufkurs (Cost-Averaging). Bei größeren Summen ist ein Mix sinnvoll.",
    },
  ],

  "immobilien-kapitalanlage": [
    {
      q: "Lohnt sich eine Immobilie als Kapitalanlage?",
      a: "Bei der richtigen Lage und Finanzierung ja – Du nutzt den Hebeleffekt durch Fremdkapital, baust Vermögen über Tilgung auf, erzielst Mieteinnahmen und nutzt Steuervorteile (Abschreibung, absetzbare Zinsen). Wichtig sind ein Eigenkapitalanteil von 10–25 % und ein realistisches Cashflow-Modell.",
    },
    {
      q: "Wie viel Eigenkapital brauche ich für eine Anlageimmobilie?",
      a: "Mindestens die Kaufnebenkosten (Grunderwerbsteuer, Notar, Makler) von 7–15 % des Kaufpreises. Sinnvoll sind weitere 10–20 % als Eigenkapital – das verbessert die Konditionen und schafft Puffer für Mietausfälle oder Sanierungen.",
    },
    {
      q: "Welche Steuervorteile bietet eine Anlageimmobilie?",
      a: "Du kannst Zinsen, Verwaltungskosten, Erhaltungsaufwand und die Abschreibung (2 % p. a. linear, höher bei Denkmal/Neubau) von den Mieteinnahmen abziehen. So entstehen in den ersten Jahren oft steuerliche Verluste, die Du mit anderem Einkommen verrechnen kannst.",
    },
    {
      q: "Wo lohnen sich Immobilieninvestments aktuell?",
      a: "In wachstumsstarken B- und C-Städten mit positiver Demografie und stabiler Wirtschaft – z. B. Leipzig, Magdeburg, Erfurt, Halle, mittlere Universitätsstädte. A-Städte wie München oder Hamburg bieten oft nur 2–3 % Mietrendite, während in B-Lagen 4–6 % erreichbar sind.",
    },
    {
      q: "Was ist der Unterschied zwischen Direktinvestment und Immobilienfonds?",
      a: "Beim Direktinvestment kaufst Du eine Immobilie selbst – mit voller Kontrolle, Hebelwirkung und Steuervorteilen, aber auch Aufwand. Immobilienfonds (offen oder geschlossen) bieten Beteiligungen ohne eigene Verwaltung, aber mit deutlich niedrigeren Renditen und meist ohne Hebel.",
    },
    {
      q: "Welche Risiken hat eine Kapitalanlage-Immobilie?",
      a: "Mietausfall, Leerstand, Sanierungsstau, sinkende Immobilienpreise, steigende Zinsen bei Anschlussfinanzierung und problematische Mieter. Wir kalkulieren konservativ mit 1 Monat Leerstand pro Jahr und 1 € pro m² Instandhaltungsrücklage.",
    },
  ],

  // ─────────────── Gewerbeversicherung ───────────────
  "betriebshaftpflichtversicherung": [
    {
      q: "Brauche ich als Selbstständiger eine Betriebshaftpflicht?",
      a: "Ja – fast immer. Sobald Du Kunden oder Geschäftspartner triffst, kann ein Schaden Deine Existenz bedrohen. Bei vielen Berufen (z. B. Handwerk, Beratung) ist sie de facto Pflicht, bei Kammerberufen oft formell vorgeschrieben.",
    },
    {
      q: "Was kostet eine Betriebshaftpflicht?",
      a: "Für kleine Betriebe und Einzelunternehmer meist 150–600 € jährlich, je nach Branche und Umsatz. Risikoreiche Branchen (Bau, Elektro) zahlen deutlich mehr, Bürotätigkeiten weniger.",
    },
    {
      q: "Wie hoch sollte die Deckungssumme sein?",
      a: "Mindestens 3 Mio. € pauschal, besser 10 Mio. €. Personenschäden – etwa wenn ein Kunde im Geschäft stürzt – können schnell sechs- bis siebenstellige Summen erreichen.",
    },
    {
      q: "Was ist der Unterschied zur Privathaftpflicht?",
      a: "Die Privathaftpflicht greift nur im privaten Bereich – berufliche Tätigkeiten sind ausdrücklich ausgeschlossen. Sobald Du als Selbstständiger einen Schaden verursachst, brauchst Du eine Betriebshaftpflicht oder Berufshaftpflicht.",
    },
    {
      q: "Was deckt eine Betriebshaftpflicht typischerweise ab?",
      a: "Personen-, Sach- und Vermögensfolgeschäden, die Du oder Mitarbeiter gegenüber Dritten verursachen. Inklusive Tätigkeits-, Mietsach- und Bearbeitungsschäden in guten Tarifen.",
    },
    {
      q: "Brauche ich zusätzlich eine Vermögensschadenhaftpflicht?",
      a: "Wenn Du beratend tätig bist (IT, Steuerberater, Anwälte, Architekten) ja – reine Vermögensschäden ohne vorherigen Personen- oder Sachschaden sind in der Betriebshaftpflicht meist nicht abgedeckt.",
    },
  ],

  "vermoegensschadenhaftpflicht": [
    {
      q: "Was ist eine Vermögensschadenhaftpflicht?",
      a: "Sie schützt vor finanziellen Schäden, die Du durch Beratungsfehler bei Kunden verursachst – z. B. fehlerhafte IT-Konfiguration, falsche Steuerberatung oder Planungsfehler. Diese reinen Vermögensschäden sind in der normalen Betriebshaftpflicht meist ausgeschlossen.",
    },
    {
      q: "Für wen ist sie sinnvoll oder Pflicht?",
      a: "Pflicht für viele Kammerberufe: Anwälte, Steuerberater, Wirtschaftsprüfer, Architekten, Notare. Dringend empfohlen für IT-Berater, Unternehmensberater, Coaches und alle, die mit ihrer Expertise Entscheidungen ihrer Kunden beeinflussen.",
    },
    {
      q: "Wie hoch sollte die Deckungssumme sein?",
      a: "Mindestens 250.000 € für kleinere Selbstständige, 1–5 Mio. € für IT- und Unternehmensberater. Bei Kammerberufen gelten gesetzliche Mindestdeckungen (z. B. 250.000 € für Anwälte).",
    },
    {
      q: "Was kostet eine Vermögensschadenhaftpflicht?",
      a: "Je nach Berufsbild und Umsatz 300–2.000 € jährlich. IT-Berater oder kleine Beratungsfirmen kommen oft mit 500–800 € jährlich gut weg.",
    },
    {
      q: "Was sind typische Schadenbeispiele?",
      a: "Falsche Steuerberatung kostet den Mandanten Strafzahlungen, eine fehlerhafte IT-Migration verursacht Datenverlust, ein Architekt unterschätzt Baukosten erheblich. Solche Fälle können schnell sechsstellige Schadensummen erreichen.",
    },
    {
      q: "Was ist der Unterschied zu D&O?",
      a: "Vermögensschadenhaftpflicht schützt Dich als beratendes Unternehmen vor Ansprüchen Deiner Kunden. D&O (Directors & Officers) schützt Geschäftsführer und Vorstände persönlich vor Ansprüchen ihrer eigenen Gesellschaft, von Mitarbeitern oder Aktionären.",
    },
  ],

  "firmenrechtsschutz": [
    {
      q: "Was deckt der Firmenrechtsschutz ab?",
      a: "Typische Bausteine: Arbeitsrecht (Klagen von Mitarbeitern), Vertragsrecht (Streit mit Kunden/Lieferanten), Steuer- und Sozialrecht, Verkehrsrecht für Firmenfahrzeuge sowie Inkasso-Rechtsschutz. Ohne diesen Schutz können einzelne Prozesse fünfstellige Beträge kosten.",
    },
    {
      q: "Wann lohnt sich Firmenrechtsschutz?",
      a: "Spätestens mit dem ersten Mitarbeiter. Arbeitsgerichtsprozesse sind häufig und teuer – jeder zweite Kündigungsschutzprozess endet mit einer Abfindung von durchschnittlich 0,5 Bruttomonatsgehältern pro Beschäftigungsjahr.",
    },
    {
      q: "Was kostet Firmenrechtsschutz?",
      a: "Für Kleinbetriebe meist 400–1.200 € jährlich, je nach Branche, Mitarbeiterzahl und Umsatz. Bei mehreren Standorten oder höherem Streitrisiko entsprechend mehr.",
    },
    {
      q: "Sind Streitigkeiten mit dem Finanzamt mitversichert?",
      a: "Nur mit dem Baustein Steuerrechtsschutz vor Finanzgerichten – wichtig vor allem für Selbstständige und Unternehmen mit komplexen Steuerthemen. Beratung vor Klageerhebung ist oft nicht inkludiert.",
    },
    {
      q: "Was sind typische Wartezeiten?",
      a: "Im Arbeits-, Miet-, Sozial- und Steuerrecht meist 3 Monate. Streitigkeiten, die schon bei Vertragsabschluss bekannt waren, sind nie versichert – wichtig also, frühzeitig abzuschließen.",
    },
    {
      q: "Was ist Strafrechtsschutz für Unternehmer?",
      a: "Er übernimmt Anwalts- und Verteidigungskosten bei Vorwürfen wie fahrlässiger Körperverletzung, Insolvenzverschleppung oder Steuerhinterziehung – sofern der Vorwurf nicht zutrifft oder nur grobe Fahrlässigkeit nachgewiesen wird.",
    },
  ],

  "gewerbliche-gebaeudeversicherung": [
    {
      q: "Was deckt eine gewerbliche Gebäudeversicherung ab?",
      a: "Sie schützt Betriebsgebäude vor Feuer, Leitungswasser, Sturm/Hagel und – als Zusatz – Elementarschäden. Inkludiert sind fest verbaute Bestandteile, oft auch Außenanlagen, Photovoltaik und Mietausfall bei Unbewohnbarkeit.",
    },
    {
      q: "Was ist der Unterschied zur privaten Wohngebäudeversicherung?",
      a: "Gewerbliche Tarife berücksichtigen die spezifischen Risiken von Betrieben (höheres Brandrisiko, Maschinen, Lager). Eine private Wohngebäudepolice greift bei gewerblicher Nutzung oft nicht oder kürzt die Leistung.",
    },
    {
      q: "Wie hoch sollte die Versicherungssumme sein?",
      a: "Der gleitende Neuwert (Wiederaufbaukosten zum heutigen Preis) – nicht der Marktwert. Bei einem 1.000 m² Gewerbebau also oft 1,5–3 Mio. € Versicherungswert, abhängig von Bauart und Ausstattung.",
    },
    {
      q: "Ist Elementarschutz für Gewerbeimmobilien sinnvoll?",
      a: "Fast immer ja – Starkregen und Rückstau führen bei Gewerbeobjekten zu hohen Schäden an Lager und Technik. Mehrkosten meist 10–30 % der Grundprämie, je nach ZÜRS-Gefahrenklasse.",
    },
    {
      q: "Was passiert bei längerem Betriebsausfall nach einem Schaden?",
      a: "Eine Betriebsunterbrechungs­versicherung (BU/BUFT) ist meist nötig – sie ersetzt entgangenen Gewinn und laufende Kosten während der Wiederherstellung. Ohne diesen Baustein gehen viele Betriebe nach einem Großschaden insolvent.",
    },
    {
      q: "Was kostet eine gewerbliche Gebäudeversicherung?",
      a: "Stark abhängig von Branche, Bauart, Größe und Lage. Für ein 800 m² Büro- oder Verkaufsgebäude meist 1.000–3.000 € jährlich, bei Produktionsbetrieben deutlich mehr.",
    },
  ],

  "elektronikversicherung": [
    {
      q: "Was deckt die Elektronikversicherung ab?",
      a: "Alle stationären und mobilen elektronischen Geräte – Server, Computer, medizinische Geräte, Kassensysteme, mobile Endgeräte. Versichert sind Schäden durch Bedienungsfehler, Kurzschluss, Überspannung, Diebstahl, Brand und Vandalismus.",
    },
    {
      q: "Worin liegt der Unterschied zur Inhaltsversicherung?",
      a: "Die Inhaltsversicherung deckt Standardrisiken wie Feuer, Einbruch, Leitungswasser. Die Elektronikversicherung greift zusätzlich bei typischen Elektronik-Risiken: Kurzschluss, Überspannung, Bedienungsfehler, innere Schäden – das ist bei moderner Technik der häufigste Schadenfall.",
    },
    {
      q: "Für wen lohnt sich die Elektronikversicherung?",
      a: "Vor allem für Arzt- und Zahnarztpraxen, Apotheken, IT-Dienstleister, Gastronomie mit komplexer Kassentechnik, Architekten und Ingenieurbüros mit teurer CAD-Hardware. Faustregel: ab 25.000 € Anschaffungswert lohnt sich der Schutz fast immer.",
    },
    {
      q: "Was kostet eine Elektronikversicherung?",
      a: "Etwa 1–3 % p. a. des Versicherungswerts. Bei 50.000 € Elektronik also 500–1.500 € jährlich, je nach Risikoklasse und Selbstbeteiligung.",
    },
    {
      q: "Sind mobile Geräte unterwegs mitversichert?",
      a: "Mit dem Baustein Außenversicherung ja – Laptops, Tablets und Smartphones bleiben weltweit oder zumindest europaweit geschützt. Wichtig vor allem für Außendienst, Berater und Handwerker.",
    },
    {
      q: "Ist Datenverlust mitversichert?",
      a: "Standardmäßig wird oft nur die Wiederherstellung von Daten auf bestehenden Trägern erstattet. Für umfassenden Datenschutz und Cyber-Risiken ist eine separate Cyberversicherung notwendig.",
    },
  ],

  "maschinenbruchversicherung": [
    {
      q: "Was deckt die Maschinenbruchversicherung ab?",
      a: "Schäden an stationären Produktions- und Betriebsmaschinen durch Bedienungsfehler, Materialermüdung, Konstruktionsfehler, Kurzschluss, Frost, Wasser und Sabotage. Versichert ist der gleitende Neuwert plus Folgekosten der Wiederinstandsetzung.",
    },
    {
      q: "Für wen lohnt sich diese Versicherung?",
      a: "Produktionsbetriebe, Druckereien, Schreinereien, KFZ-Werkstätten, Landwirtschaft – überall, wo Maschinen mit hohem Anschaffungswert das Kerngeschäft tragen. Ein Ausfall kann Produktionsstopps und Umsatzverluste in fünf- bis sechsstelliger Höhe verursachen.",
    },
    {
      q: "Was kostet eine Maschinenbruchversicherung?",
      a: "Je nach Risikoklasse und Versicherungswert ca. 1,5–4 % p. a. Bei 200.000 € Maschinenwert also 3.000–8.000 € jährlich. Mit höherer Selbstbeteiligung lassen sich die Prämien deutlich senken.",
    },
    {
      q: "Was ist die Betriebsunterbrechungs­versicherung dazu?",
      a: "Sie deckt entgangenen Gewinn und laufende Kosten während des Maschinenausfalls. Bei Großmaschinen oder Engpass-Anlagen oft wichtiger als die reine Sachversicherung – ein Maschinenausfall von 6 Wochen kann den Jahresgewinn vernichten.",
    },
    {
      q: "Sind Verschleiß und Wartung mitversichert?",
      a: "Nein – normale Abnutzung, Korrosion und Verschleiß sind generell ausgeschlossen. Versichert sind nur plötzliche und unvorhergesehene Schäden.",
    },
    {
      q: "Gilt der Schutz auch bei Inbetriebnahme und Probelauf?",
      a: "Mit dem entsprechenden Baustein ja. Wichtig bei Neuanschaffungen, da gerade in dieser Phase überdurchschnittlich viele Schäden auftreten.",
    },
  ],

  "it-haftpflichtversicherung": [
    {
      q: "Was ist eine IT-Haftpflichtversicherung?",
      a: "Eine Kombination aus Betriebs-, Vermögensschaden- und Produkt­haftpflicht speziell für IT-Dienstleister. Sie schützt vor finanziellen Folgen aus fehlerhafter Programmierung, Beratungsfehlern, Migrationsproblemen und Datenverlust.",
    },
    {
      q: "Brauchen Freelancer und kleine IT-Unternehmen das?",
      a: "Unbedingt – schon bei einem mittelgroßen Kunden kann ein Fehler in Software, Migration oder Hosting Folgeschäden in fünf- bis sechsstelliger Höhe verursachen. Viele Großkunden setzen die Police vertraglich voraus.",
    },
    {
      q: "Wie hoch sollte die Deckungssumme sein?",
      a: "Mindestens 500.000 €, besser 1–5 Mio. € pauschal. Bei größeren Projekten oder Geschäft mit Konzernen werden oft 5–10 Mio. € verlangt.",
    },
    {
      q: "Was kostet eine IT-Haftpflicht?",
      a: "Für Solo-Freelancer ab ca. 500–800 € jährlich, kleine IT-Firmen mit bis zu 10 Mitarbeitern meist 1.000–3.000 € pro Jahr. Spezialleistungen wie Cyber-Risiken oder Open-Source-Klauseln erhöhen den Beitrag.",
    },
    {
      q: "Sind Open-Source-Risiken mitversichert?",
      a: "Nur in spezialisierten IT-Tarifen mit entsprechender Klausel. Wichtig vor allem bei kommerzieller Nutzung von Open-Source-Bibliotheken oder eigenen Open-Source-Beiträgen – unbeabsichtigte Lizenzverletzungen können teuer werden.",
    },
    {
      q: "Was ist mit Cybervorfällen wie Hackerangriffen?",
      a: "Klassische IT-Haftpflicht deckt nicht Deine eigenen Schäden durch Cyberangriffe – dafür ist eine separate Cyberversicherung nötig. Sinnvoll ist die Kombination beider Policen.",
    },
  ],

  "cyberversicherung": [
    {
      q: "Was deckt eine Cyberversicherung ab?",
      a: "Eigenschäden (Datenwiederherstellung, Betriebsunterbrechung, Krisenmanagement, Lösegeld bei Ransomware) und Drittschäden (Haftung gegenüber Kunden bei Datenverlust, DSGVO-Bußgeldern). Plus 24/7-Hotline und Forensik nach einem Angriff.",
    },
    {
      q: "Brauchen kleine Unternehmen wirklich eine Cyberversicherung?",
      a: "Ja – 60 % aller erfolgreichen Cyberangriffe treffen kleine und mittelständische Unternehmen. Die durchschnittlichen Schadenkosten liegen bei 50.000–250.000 €. Schon eine einzelne Ransomware-Attacke kann existenzbedrohend sein.",
    },
    {
      q: "Was kostet eine Cyberversicherung?",
      a: "Für kleine Unternehmen ab 500 € jährlich, für Mittelständler je nach Branche und Sicherheitsniveau 2.000–10.000 €. Voraussetzung ist ein Mindeststandard an IT-Sicherheit (Backups, Patches, Mitarbeiterschulungen, MFA).",
    },
    {
      q: "Sind DSGVO-Bußgelder versicherbar?",
      a: "In Deutschland überwiegend ja, sofern es sich nicht um vorsätzliche Verstöße handelt. Wichtige Klausel im Tarif – nicht jeder Versicherer übernimmt Bußgelder in voller Höhe.",
    },
    {
      q: "Was ist mit Lösegeldzahlungen bei Ransomware?",
      a: "Manche Tarife übernehmen Lösegeld, andere nicht. Viel wichtiger ist die Wiederherstellung von Systemen und Daten ohne Zahlung – gute Tarife stellen Krisenteams, IT-Forensik und Backup-Dienste bereit.",
    },
    {
      q: "Was sind typische Voraussetzungen für den Abschluss?",
      a: "Regelmäßige Backups (idealerweise offline), aktuelle Patches, Antivirensoftware, MFA für Admin-Zugänge, Mitarbeiterschulungen zu Phishing. Ohne diese Mindeststandards verweigern viele Versicherer den Abschluss oder kürzen im Schadenfall.",
    },
  ],

  "do-versicherung": [
    {
      q: "Was ist eine D&O-Versicherung?",
      a: "Directors-and-Officers-Versicherung – sie schützt Geschäftsführer und Vorstände persönlich vor Schadenersatzansprüchen aus ihrer Tätigkeit. Versichert sind Innenhaftung (Ansprüche des Unternehmens selbst) und Außenhaftung (Aktionäre, Gläubiger, Behörden).",
    },
    {
      q: "Wer braucht eine D&O-Versicherung?",
      a: "Jeder Geschäftsführer einer GmbH, Vorstand einer AG sowie leitende Angestellte mit Entscheidungs­kompetenz. Auch ehrenamtliche Vorstände in Vereinen oder Stiftungen sind oft persönlich haftbar und brauchen Schutz.",
    },
    {
      q: "Warum reicht die normale Betriebshaftpflicht nicht?",
      a: "Die Betriebshaftpflicht schützt die Gesellschaft, nicht die Geschäftsführer persönlich. Bei Pflichtverletzungen haftest Du mit Deinem Privatvermögen – unbeschränkt und gesamtschuldnerisch.",
    },
    {
      q: "Was kostet eine D&O-Versicherung?",
      a: "Für kleine GmbHs ab ca. 800–1.500 € jährlich für 1 Mio. € Deckung. Bei mittelständischen Unternehmen oder höheren Deckungssummen entsprechend mehr.",
    },
    {
      q: "Was sind typische Schadenbeispiele?",
      a: "Versäumte Insolvenzantragsstellung, falsche steuerliche Entscheidungen, fehlerhafte Bilanzierung, Verstöße gegen Compliance-Pflichten, Schadenersatz wegen Fehlbesetzung von Führungspositionen.",
    },
    {
      q: "Wirkt die D&O auch rückwirkend für frühere Tätigkeiten?",
      a: "Mit der sogenannten Rückwärtsdeckung ja – wichtig für neue Geschäftsführer, die alte Pflichtverletzungen ihres Vorgängers nicht ausschließen können. Standard sind 12–36 Monate Rückwärtsdeckung.",
    },
  ],

  "berufshaftpflicht-aerzte": [
    {
      q: "Brauche ich als angestellter Arzt eine eigene Berufshaftpflicht?",
      a: "Im Krankenhaus bist Du meist über den Arbeitgeber versichert – aber nur für die Tätigkeit dort. Für Nebentätigkeiten, Vertretungen, Honorartätigkeit und Notfälle außerhalb der Klinik brauchst Du eine eigene Police.",
    },
    {
      q: "Was kostet die Berufshaftpflicht für Ärzte?",
      a: "Stark abhängig vom Fachgebiet. Hausärzte zahlen ca. 300–800 € jährlich, Geburtshelfer und Anästhesisten oft 2.000–10.000 € oder mehr – wegen des deutlich höheren Schadenrisikos.",
    },
    {
      q: "Welche Deckungssumme ist sinnvoll?",
      a: "Mindestens 3 Mio. €, in vielen Fachgebieten 5–10 Mio. €. Bei Geburtshilfe sind oft 15 Mio. € nötig, da bleibende Geburtsschäden lebenslange Versorgungsansprüche auslösen können.",
    },
    {
      q: "Sind ästhetische Eingriffe mitversichert?",
      a: "Nur mit ausdrücklicher Vereinbarung – ästhetische und Lifestyle-Medizin erhöhen das Risiko und damit den Beitrag. Ohne Klausel besteht kein Schutz für diese Tätigkeiten.",
    },
    {
      q: "Was deckt die Berufshaftpflicht konkret ab?",
      a: "Personen-, Sach- und Vermögensfolgeschäden durch ärztliche Behandlungsfehler. Inklusive Strafrechtsschutz, Schiedsstellenverfahren und Schutz vor Gutachterkosten bei strittigen Fällen.",
    },
    {
      q: "Gilt der Schutz auch im Ausland?",
      a: "Innerhalb der EU meist ja, weltweit nur bei explizit vereinbartem Auslandsschutz. Wichtig für Vertretungen, humanitäre Einsätze und Konsiliartätigkeit im Ausland.",
    },
  ],

  "kfz-flottenversicherung": [
    {
      q: "Was ist eine KFZ-Flottenversicherung?",
      a: "Ein einheitlicher Versicherungsvertrag für alle Firmenfahrzeuge eines Unternehmens – meist ab 3–5 Fahrzeugen sinnvoll. Statt einzelner SF-Klassen gilt eine gemeinsame Schadenquote, die jährlich neu berechnet wird.",
    },
    {
      q: "Ab wann lohnt sich eine Flottenversicherung?",
      a: "Meist ab 5 Fahrzeugen – dann sind die Konditionen oft 10–30 % günstiger als Einzelverträge. Bei sehr jungen Fahrern oder neuen Fahrzeugen mit hohem Anteil an Schadenfreiheit-Rückstufung kann sich der Wechsel schon ab 3 Fahrzeugen lohnen.",
    },
    {
      q: "Was sind die Vorteile einer Flottenversicherung?",
      a: "Einheitlicher Vertrag, ein Ansprechpartner, gleiche Bedingungen für alle Fahrzeuge, einfaches An- und Abmelden neuer Fahrzeuge, oft attraktivere Prämien und Sonderbedingungen für Schadenfreiheit.",
    },
    {
      q: "Wie wird die Prämie berechnet?",
      a: "Auf Basis der Gesamtschadenquote der letzten 3–5 Jahre. Bei guter Schadenfreiheit sinken die Beiträge spürbar, bei vielen Schäden steigen sie ähnlich stark – eine kollektive Verantwortung im gesamten Fuhrpark.",
    },
    {
      q: "Was passiert bei einem Fahrerwechsel oder Neufahrzeug?",
      a: "Neue Fahrzeuge werden einfach in den Rahmenvertrag aufgenommen, oft mit sofortigem Versicherungsschutz. Fahrerwechsel sind unproblematisch, da die Versicherung am Fahrzeug hängt, nicht am einzelnen Fahrer.",
    },
    {
      q: "Welche Bausteine sind für Flotten besonders wichtig?",
      a: "Werkstattbindung (oft 20 % Ersparnis), Auslandsschadenschutz, Schutzbrief, GAP-Deckung bei Leasingfahrzeugen, Telematik für Schadenprävention und Fuhrparkmanagement-Tools zur Übersicht.",
    },
  ],

  // ─────────────── Finanzierung ───────────────
  "immobilienkredit": [
    {
      q: "Wie viel Eigenkapital brauche ich für eine Immobilienfinanzierung?",
      a: "Idealerweise mindestens die Kaufnebenkosten (Grunderwerbsteuer, Notar, Makler) von 7–15 % plus 10–20 % vom Kaufpreis. Eine 100%-Finanzierung ist möglich, aber teurer – die Konditionen verbessern sich deutlich ab 20 % Eigenkapital.",
    },
    {
      q: "Wie hoch sollte die Tilgung sein?",
      a: "Aktuell mindestens 2 % anfängliche Tilgung, besser 3–4 %. Bei 2 % Tilgung und 3,5 % Zins läuft die Finanzierung über 30 Jahre – die meisten möchten vor dem Renteneintritt schuldenfrei sein.",
    },
    {
      q: "Welche Zinsbindung ist sinnvoll?",
      a: "Bei stabilen oder steigenden Zinsen 15–20 Jahre, bei sehr hohen Zinsen kürzer (10 Jahre). Mit langer Zinsbindung sicherst Du Dir Planungssicherheit über die gesamte Laufzeit – nach 10 Jahren ist eine Kündigung mit 6 Monaten Frist sowieso möglich (§ 489 BGB).",
    },
    {
      q: "Sind Sondertilgungsrechte wichtig?",
      a: "Ja – 5 % p. a. Sondertilgung kosten meist nichts und geben Flexibilität bei Boni, Erbschaften oder Gehaltssprüngen. Höhere Sondertilgungsrechte kosten oft nur 0,01–0,05 % Zinsaufschlag und lohnen sich fast immer.",
    },
    {
      q: "Was kostet mich ein Immobilienkredit aktuell?",
      a: "Stand 2026 liegen die Zinsen für 10-jährige Zinsbindung typischerweise bei 3,0–4,0 % p. a., für 20 Jahre bei 3,3–4,3 %. Die exakten Konditionen hängen von Eigenkapital, Bonität und Objekt ab.",
    },
    {
      q: "Lohnt sich ein Forward-Darlehen?",
      a: "Wenn Du in 1–5 Jahren in die Anschlussfinanzierung musst und mit steigenden Zinsen rechnest: ja. Pro Vorausjahr fallen meist 0,01–0,03 % Zinsaufschlag an – bei stark steigenden Marktzinsen kann das deutlich günstiger sein als zu warten.",
    },
  ],

  "konsumkredit": [
    {
      q: "Wann lohnt sich ein Konsumkredit?",
      a: "Bei zwingend notwendigen, sofort fälligen Anschaffungen (defektes Auto, kaputte Heizung). Für reine Konsumwünsche (Urlaub, Möbel, Elektronik) ist Sparen fast immer die bessere Lösung – Konsumkredite kosten oft 4–8 % p. a.",
    },
    {
      q: "Wie hoch sind die Zinsen für einen Konsumkredit?",
      a: "Bei guter Bonität typischerweise 3,5–6 % p. a. effektiv, bei schwächerer Bonität 7–12 %. Online-Banken und Vergleichsportale bieten oft die günstigsten Konditionen.",
    },
    {
      q: "Was ist der Unterschied zwischen Effektiv- und Sollzins?",
      a: "Der Sollzins ist der reine Zinssatz, der Effektivzins enthält zusätzlich alle Nebenkosten (Bearbeitungs­gebühren, Restschuldversicherung). Für den Vergleich ist nur der Effektivzins relevant – er zeigt die tatsächlichen Gesamtkosten.",
    },
    {
      q: "Lohnt sich eine Restschuldversicherung beim Kredit?",
      a: "Meist nein – sie ist oft überteuert und verschlechtert den Effektivzins deutlich. Sinnvoller sind eine vorhandene Risikolebens­versicherung und Berufsunfähigkeitsversicherung, die alle Kreditverpflichtungen abdecken.",
    },
    {
      q: "Kann ich einen Konsumkredit jederzeit zurückzahlen?",
      a: "Ja – Verbraucherkredite können jederzeit ganz oder teilweise vorzeitig zurückgezahlt werden. Die Bank darf höchstens 1 % der vorzeitig zurückgezahlten Summe als Vorfälligkeits­entschädigung verlangen (bei Restlaufzeit über 1 Jahr).",
    },
    {
      q: "Wie verbessere ich meine Chancen auf günstige Konditionen?",
      a: "Saubere SCHUFA, längere Beschäftigung, Sparkonto-Nachweise, kein Dispo-Überzug und realistische Kredithöhe. Ein Vergleich mehrerer Anbieter (möglichst über konditionsneutrale Anfragen) spart oft 1–3 Prozentpunkte Effektivzins.",
    },
  ],

  // ─────────────── Neue Einträge ───────────────
  "erwerbsminderungsversicherung": [
    {
      q: "Was ist eine Erwerbsminderungsversicherung?",
      a: "Sie zahlt eine vereinbarte Rente, wenn Du aus gesundheitlichen Gründen gar keiner Tätigkeit auf dem allgemeinen Arbeitsmarkt mehr nachgehen kannst – nicht nur Deinem aktuellen Beruf wie bei der Berufsunfähigkeitsversicherung. Die Hürde ist höher, die Beiträge dafür meist 30–50 % günstiger.",
    },
    {
      q: "Was ist der Unterschied zur Berufsunfähigkeitsversicherung?",
      a: "Die Berufsunfähigkeitsversicherung leistet, wenn Du Deinen zuletzt ausgeübten Beruf zu mindestens 50 % nicht mehr ausüben kannst. Die Erwerbsminderungs­versicherung erst, wenn Du gar nicht mehr arbeiten kannst. Wenn möglich, ist die Berufsunfähigkeitsversicherung fast immer die bessere Wahl – die Erwerbsminderungsversicherung ist Plan B.",
    },
    {
      q: "Reicht die gesetzliche Erwerbsminderungsrente nicht aus?",
      a: "Nein – die durchschnittliche volle Erwerbsminderungsrente liegt unter 1.000 € im Monat. Bei teilweiser Erwerbsminderung sogar nur die Hälfte. Außerdem brauchst Du mindestens 5 Jahre Beitragszeit, Berufsstarter:innen haben oft gar keinen Anspruch.",
    },
    {
      q: "Für wen lohnt sich die Erwerbsminderungsversicherung?",
      a: "Vor allem für Menschen mit Vorerkrankungen, Risikoberufen oder kleinem Budget, die keine bezahlbare Berufsunfähigkeitsversicherung bekommen. Auch als Ergänzung zur gesetzlichen Erwerbsminderungsrente, um die Versorgungslücke zu schließen.",
    },
    {
      q: "Wie hoch sollte die Rente sein?",
      a: "Faustregel: mindestens 60–70 % Deines Nettoeinkommens. Bei Existenzsicherung reichen oft 1.500–2.000 € im Monat. Je früher Du abschließt, desto günstiger der Beitrag.",
    },
    {
      q: "Werden Vorerkrankungen ein Problem?",
      a: "Tendenziell weniger als bei der Berufsunfähigkeitsversicherung – die Annahmequote ist höher, Risikozuschläge moderater. Wir prüfen Deinen Gesundheitsstatus anonym über eine Risikovoranfrage bei mehreren Anbietern.",
    },
  ],

  "dienstunfaehigkeitsversicherung": [
    {
      q: "Was ist eine Dienstunfähigkeitsversicherung?",
      a: "Eine spezielle Absicherung für Beamt:innen, die zahlt, sobald Dich der Dienstherr in den Ruhestand versetzt. Sie funktioniert wie eine Berufsunfähigkeitsversicherung – nur mit echter DU-Klausel, sodass die Entscheidung Deines Dienstherrn vom Versicherer übernommen wird.",
    },
    {
      q: "Warum reicht eine normale Berufsunfähigkeitsversicherung für Beamte nicht?",
      a: "Ohne echte DU-Klausel kann der Versicherer Deine Dienstunfähigkeit anders bewerten als der Dienstherr – im Streitfall bekommst Du dann keine Leistung. Nur Tarife mit echter DU-Klausel übernehmen die Entscheidung Deines Dienstherrn vollständig.",
    },
    {
      q: "Was ist der Unterschied zwischen echter und unechter DU-Klausel?",
      a: "Echte DU-Klausel: Versicherer erkennt die Entscheidung Deines Dienstherrn ohne eigene Prüfung an. Unechte DU-Klausel: Versicherer behält sich eine eigene Prüfung vor – im Streitfall hilft sie wenig. Achte beim Vergleich unbedingt auf die echte Klausel.",
    },
    {
      q: "Ab wann sollte ich abschließen?",
      a: "So früh wie möglich – idealerweise schon als Anwärter:in oder Beamt:in auf Widerruf. Je jünger und gesünder Du bist, desto günstiger und besser sind die Konditionen. Wichtig ist die Nachversicherungsgarantie bei späterer Verbeamtung auf Lebenszeit.",
    },
    {
      q: "Was kostet eine Dienstunfähigkeitsversicherung?",
      a: "Für eine:n 28-jährige:n Lehrer:in mit 2.000 € Rente oft 60–110 € monatlich. Bei Polizist:innen und Soldat:innen häufig etwas teurer aufgrund höherer Risikoeinstufung.",
    },
    {
      q: "Bin ich auch als Beamt:in auf Probe abgesichert?",
      a: "Ja – Top-Tarife leisten auch in der Probezeit. Wichtig ist, dass der Tarif explizit eine Klausel für Beamte auf Probe und Widerruf enthält, sonst greift der Schutz erst nach Verbeamtung auf Lebenszeit.",
    },
  ],

  "riester-rente": [
    {
      q: "Lohnt sich die Riester-Rente 2026 noch?",
      a: "Für viele Bestandskund:innen ja – vor allem Familien mit Kindern und Geringverdiener:innen profitieren weiter von Zulagen und Steuervorteilen. Für Neuverträge wird Riester ab 2027 vom Altersvorsorgedepot abgelöst, das deutlich attraktiver ist.",
    },
    {
      q: "Wie hoch ist die Riester-Förderung?",
      a: "Grundzulage 175 € pro Jahr, Kinderzulage 300 € pro Kind (geboren ab 2008) und Steuerersparnis durch Sonderausgabenabzug bis 2.100 € im Jahr. Für volle Zulagen musst Du 4 % Deines Brutto-Vorjahreseinkommens einzahlen.",
    },
    {
      q: "Soll ich meinen Riester-Vertrag kündigen?",
      a: "In den meisten Fällen nein – bei Kündigung musst Du alle Zulagen und Steuervorteile zurückzahlen. Sinnvoller ist eine Beitragsfreistellung oder Reduzierung. Wir prüfen Deinen Bestandsvertrag individuell.",
    },
    {
      q: "Was ist der Unterschied zum Altersvorsorgedepot 2027?",
      a: "Riester verlangt eine 100 %-Beitragsgarantie zum Rentenbeginn – das verhindert hohe Aktienquoten und drückt die Rendite. Das Altersvorsorgedepot 2027 erlaubt freies ETF-Sparen ohne Garantie, mit deutlich höheren Renditechancen.",
    },
    {
      q: "Kann ich Riester später ins Altersvorsorgedepot übertragen?",
      a: "Die Politik diskutiert genau diese Frage. Für Bestandskund:innen soll ein steueroptimierter Übertragungsweg ins neue Depot ermöglicht werden. Wir verfolgen die Entwicklung und melden uns, sobald die Details feststehen.",
    },
    {
      q: "Was passiert mit meinem Riester-Vertrag im Erbfall?",
      a: "Eheleute können das Guthaben steuerneutral auf den eigenen Riester-Vertrag übertragen. Andere Erben erhalten das Guthaben nur unter Rückzahlung aller Zulagen und Steuervorteile.",
    },
  ],

  "altersvorsorgedepot-2027": [
    {
      q: "Was ist das Altersvorsorgedepot 2027?",
      a: "Ein staatlich gefördertes Depot, in dem Du ab 2027 gezielt in ETFs und Fonds für die Altersvorsorge investieren kannst. Es ersetzt die Riester-Rente und kombiniert die staatlichen Zulagen mit der Renditechance des Kapitalmarkts – ohne teure Garantiekosten.",
    },
    {
      q: "Wer ist förderberechtigt?",
      a: "Wie bei Riester: rentenversicherungspflichtige Angestellte, Beamt:innen, Eltern in Elternzeit und mittelbar Begünstigte (z. B. nicht erwerbstätige Ehepartner). Selbstständige bleiben außen vor – für sie ist die Rürup-Rente meist die bessere Wahl.",
    },
    {
      q: "Was ist der Vorteil gegenüber Riester?",
      a: "Keine Beitragsgarantie, dafür echte ETF-Anlage und deutlich niedrigere Kosten (typisch 0,2–0,5 % statt 1,5–2,5 % p. a.). Über 30 Jahre macht das oft 30–50 % mehr Endkapital – bei gleichen Beiträgen.",
    },
    {
      q: "Wann kann ich das Altersvorsorgedepot abschließen?",
      a: "Voraussichtlich ab 1. Januar 2027 – sobald die ersten Anbieter ihre Tarife auf den Markt bringen. Bis dahin verfolgen wir die Marktentwicklung und melden uns rechtzeitig mit einem Anbietervergleich.",
    },
    {
      q: "Soll ich meinen Riester-Vertrag dafür kündigen?",
      a: "Bitte nicht voreilig – bei Kündigung gehen alle Zulagen und Steuervorteile verloren. Sinnvoll ist meist eine Beitragsfreistellung des Altvertrags und Neusparen im Altersvorsorgedepot. Die Politik plant zudem eine steueroptimierte Übertragungsmöglichkeit.",
    },
    {
      q: "Welche ETFs sind im Altersvorsorgedepot möglich?",
      a: "Das genaue Fondsuniversum legen die Anbieter fest. Üblich werden globale Indizes wie MSCI World, All Country World, EM und Branchen-ETFs sein. Wir achten beim Vergleich auf eine breite, kostengünstige Auswahl.",
    },
    {
      q: "Was passiert, wenn die Kurse zum Rentenbeginn niedrig sind?",
      a: "Anders als Riester gibt es keine Beitragsgarantie – das Marktrisiko trägst Du selbst. Sinnvoll ist daher ein langfristiges Lifecycle-Modell, das in den letzten Jahren vor Rentenbeginn schrittweise in sicherere Anlagen umschichtet.",
    },
  ],

  "pflegezusatzversicherung": [
    {
      q: "Was ist eine Pflegezusatzversicherung?",
      a: "Eine private Versicherung, die im Pflegefall zusätzliches Geld zahlt – als Pflegetagegeld oder Pflegekosten­versicherung. Sie schließt die Lücke zwischen den realen Pflegekosten und den Leistungen der gesetzlichen Pflegeversicherung.",
    },
    {
      q: "Wie groß ist die Versorgungslücke wirklich?",
      a: "Ein vollstationärer Pflegeplatz kostet je nach Region 3.500–5.500 € im Monat. Die gesetzliche Pflegeversicherung übernimmt im Pflegegrad 5 maximal 2.096 €. Der Rest – oft 2.000 € und mehr – ist Eigenanteil.",
    },
    {
      q: "Was ist besser: Pflegetagegeld oder Pflegekostenversicherung?",
      a: "Das Pflegetagegeld ist am beliebtesten – Du bekommst einen festen Tagessatz, den Du frei verwenden kannst. Die Pflegekostenversicherung erstattet nur belegte Kosten, bietet dafür meist höhere Maximalleistungen.",
    },
    {
      q: "Ab wann sollte ich eine Pflegezusatzversicherung abschließen?",
      a: "Je früher, desto besser – idealerweise zwischen 30 und 45. Ein 35-Jähriger zahlt für 50 € Tagegeld in Pflegegrad 5 oft 25–40 € im Monat, ein 60-Jähriger das Dreifache. Ab 60 sind oft auch Vorerkrankungen ein Problem.",
    },
    {
      q: "Wer haftet, wenn ich pflegebedürftig werde?",
      a: "Reicht Deine Rente nicht, müssen erst Dein Vermögen, dann unter bestimmten Voraussetzungen die Kinder einspringen (Elternunterhalt erst ab 100.000 € Bruttojahreseinkommen des Kindes). Eine Pflegezusatzversicherung schützt Dich und Deine Familie davor.",
    },
    {
      q: "Lohnt sich der staatliche Pflege-Bahr-Tarif?",
      a: "Mit 5 € Zuschuss pro Monat (60 € pro Jahr) staatlich gefördert – allerdings mit gesetzlichen Mindestleistungen, die meist nicht ausreichen. Für die meisten ist ein normaler Tarif die bessere Wahl. Wir vergleichen beides individuell.",
    },
  ],

  "auslandsreisekrankenversicherung": [
    {
      q: "Brauche ich eine Auslandsreisekrankenversicherung?",
      a: "Ja – auch innerhalb der EU. Die gesetzliche Krankenversicherung zahlt im Ausland oft nur eingeschränkt, außerhalb der EU meist gar nicht. Vor allem der medizinisch notwendige Rücktransport wird grundsätzlich nicht übernommen – und kostet aus Übersee schnell 50.000 € und mehr.",
    },
    {
      q: "Was kostet eine Auslandsreisekrankenversicherung?",
      a: "Eine Jahrespolice für Singles gibt es ab etwa 10 €, für Familien ab 25–40 € pro Jahr. Damit bist Du bei jeder Reise unter 6–8 Wochen Dauer geschützt – günstiger als jede Einzelreise-Versicherung.",
    },
    {
      q: "Wann zahlt der Rücktransport?",
      a: "Top-Tarife zahlen schon, wenn der Rücktransport medizinisch sinnvoll ist – nicht erst, wenn er zwingend notwendig wäre. Achte auf diese Formulierung im Tarif, das ist im Ernstfall entscheidend.",
    },
    {
      q: "Sind chronische Erkrankungen mitversichert?",
      a: "Akute Verschlechterungen einer chronischen Erkrankung sind in Top-Tarifen abgedeckt – sofern sie bei Reiseantritt stabil war. Behandlungen, die Hauptzweck der Reise sind, werden nicht übernommen.",
    },
    {
      q: "Gilt der Schutz auch in den USA?",
      a: "Ja – aber prüfe die Klauseln. Die USA und Kanada sind besonders teuer (eine einfache Notaufnahme kostet schnell 5.000 €), nicht jeder Tarif zahlt dort die vollen Kosten. Top-Tarife schließen USA/Kanada ohne Aufpreis ein.",
    },
    {
      q: "Was ist mit Langzeitreisen?",
      a: "Standard-Tarife decken meist 6–8 Wochen am Stück ab. Für Weltreisen, Sabbaticals oder Auswanderung gibt es Spezial-Tarife mit Laufzeiten bis zu 5 Jahren und Geltung weltweit – wir vergleichen sie individuell.",
    },
  ],

  "fahrradversicherung": [
    {
      q: "Brauche ich eine Fahrradversicherung – oder reicht die Hausrat?",
      a: "Die Hausrat deckt Fahrraddiebstahl nur in begrenztem Umfang (1–10 % der Versicherungssumme) und meist nur in abgeschlossenen Räumen. Wer sein Rad regelmäßig im öffentlichen Raum abstellt, braucht eine eigene Fahrradversicherung mit 24/7-Schutz weltweit.",
    },
    {
      q: "Ab welchem Radwert lohnt sich eine eigene Police?",
      a: "Faustregel: ab etwa 1.500 € Radwert oder bei jedem E-Bike. Eine Police kostet meist 4–8 % des Radwerts pro Jahr – bei E-Bikes und hochwertigen Rennrädern eine sinnvolle Investition.",
    },
    {
      q: "Sind E-Bike-Akkus mitversichert?",
      a: "In Top-Tarifen ja – ein Ersatzakku kostet schnell 600–1.500 €. Achte beim Vergleich darauf, dass Akku-Schäden, Akku-Diebstahl und Verschleiß explizit erwähnt sind.",
    },
    {
      q: "Was ist mit Unfallschäden und Vandalismus?",
      a: "Top-Tarife übernehmen Reparaturkosten nach eigenen Stürzen, Vandalismus und Wildunfällen zum Neuwert. Einige Anbieter zahlen sogar bei Verschleißschäden und Pannen vor Ort.",
    },
    {
      q: "Wie kann ich Diebstahl vermeiden?",
      a: "Hochwertiges Schloss (mind. 10 % des Radwerts in das Schloss investieren), Rad bei Möglichkeit immer anschließen, Codierung beim ADFC oder Polizei vornehmen lassen. Viele Versicherer fordern ein bestimmtes Schloss-Sicherheitslevel – sonst kann die Leistung gekürzt werden.",
    },
    {
      q: "Gilt der Schutz auch im Ausland?",
      a: "Die meisten Tarife bieten Schutz innerhalb Europas (oft 30–90 Tage), Top-Tarife sogar weltweit. Bei längeren Reisen den Tarif vorab prüfen.",
    },
  ],

  "betriebsunterbrechungsversicherung": [
    {
      q: "Was ist eine Betriebsunterbrechungs­versicherung?",
      a: "Sie zahlt fortlaufende Fixkosten (Miete, Gehälter, Leasing, Versicherungen) und entgangenen Gewinn, wenn Dein Betrieb durch einen versicherten Schaden (Brand, Wasser, Cyber-Vorfall, Maschinenbruch) stillsteht. Üblich sind Haftzeiten von 12 bis 24 Monaten.",
    },
    {
      q: "Wer braucht eine Betriebsunterbrechungs­versicherung?",
      a: "Vor allem Selbstständige, Praxen, Gastronomie, Produktionsbetriebe und Einzelhandel mit hohen Fixkosten. Auch IT-Dienstleister, die bei Cyber-Vorfällen schnell tagelang ausfallen können, profitieren stark.",
    },
    {
      q: "Was ist der Unterschied zur Betriebshaftpflicht?",
      a: "Die Betriebshaftpflicht zahlt für Schäden, die Du anderen zufügst. Die Betriebsunterbrechungs­versicherung zahlt Dir selbst – wenn Dein Betrieb durch einen versicherten Sachschaden stillsteht. Beide Policen ergänzen sich, sind aber nicht austauschbar.",
    },
    {
      q: "Wie lange wird gezahlt?",
      a: "Die sogenannte Haftzeit liegt meist bei 12 oder 24 Monaten ab Schadeneintritt. Das reicht in der Regel auch nach einem Totalschaden, um wieder produktionsfähig zu sein.",
    },
    {
      q: "Was sind versicherte Schadenereignisse?",
      a: "Klassisch: Brand, Leitungswasser, Sturm, Maschinenbruch. Mit Zusatzbausteinen auch Cyber-Vorfälle, Stromausfall oder Ausfall wichtiger Personen. Pandemien sind ohne Sonderklausel meist nicht abgedeckt.",
    },
    {
      q: "Was kostet eine Betriebsunterbrechungs­versicherung?",
      a: "Beitrag richtet sich nach Versicherungssumme (Fixkosten + Gewinn), Haftzeit und Branche. Für einen kleinen Handwerksbetrieb oft 300–800 € im Jahr, für eine Arztpraxis oder Produktionsbetrieb deutlich mehr.",
    },
    {
      q: "Kann ich sie mit der Sachversicherung kombinieren?",
      a: "Ja – meist sogar günstiger als separat. Wir bündeln die Betriebsunterbrechung mit Inhalts-, Gebäude-, Maschinen- oder Cyber-Policen beim selben Versicherer und sparen bis zu 15 % Beitrag.",
    },
  ],
};

